import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  COMPANY_TYPES,
  COMPENSATION_TYPES,
  CONTACT_TYPES,
  CompanyType,
  FOLLOWING_SIZES,
  SORTS,
  SearchParams,
  TARGET_GENDERS,
} from "schemas/dashboard";
import { ALL } from "schemas/forms";
import { LANGUAGES } from "schemas/languages";
import { routes } from "schemas/routes";

import { ON_BEHALF_OF_TOKEN } from "utils/localStorage";
import { trackEvent } from "utils/tracking";
import { usePageSize } from "utils/usePageSize";
import { useSearchBrand } from "utils/useSearchBrand";

import AutomationVA from "./AutomationVA";
import LocationFilter from "./LocationFilter";
import SelectFilter from "./SelectFilter";
import DiscoverFilterDesktop from "./desktop";
import { companyTypeIcons, contactTypeIcons } from "./helpers";
import DiscoverFilterMobile from "./mobile";
import styles from "./styles";

interface Props {
  newCampaignView?: boolean;
  totalCount?: number | null;
  brandsLoading?: boolean;
}

export default function DiscoverFilter({
  newCampaignView,
  totalCount,
  brandsLoading,
}: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { breaksDiscoverScreen } = usePageSize();
  const isSmallScreen =
    (newCampaignView && isMobileScreen) ||
    (!newCampaignView && breaksDiscoverScreen);
  const { currentOrg } = useContext(OrganizationUserContext);

  const { addCategoryQueryToSearch, selectedTags } = useSearchBrand();

  const isAdmin =
    Boolean(sessionStorage.getItem(ON_BEHALF_OF_TOKEN)) ||
    process.env.NODE_ENV === "development";

  const [close, setClose] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);

  // Hide `All` tag and sort params.
  const displayedSelectedTags = selectedTags?.filter(
    (tag) =>
      tag?.name &&
      tag?.params !== SearchParams.SORT_DISCOVER &&
      tag?.key !== ALL,
  );

  const isOptionSelected = (key: string, params: string) => {
    return Boolean(
      selectedTags?.find((x) => x?.key === key && x.params === params),
    );
  };

  const handleAddOption = (name: string, value: string) => {
    addCategoryQueryToSearch(name, value);
    setClose(true);
  };

  const selectedOptionIcon = (
    <Box
      component="i"
      className="fa-regular fa-check"
      sx={{ mr: 2, color: "success.main" }}
    />
  );

  const sortFilter = (
    <SelectFilter
      label={"Sort By"}
      options={SORTS}
      params={SearchParams.SORT_DISCOVER}
      setClose={setClose}
      close={close}
      isOptionSelected={isOptionSelected}
      selectedOptionIcon={selectedOptionIcon}
      isSmallScreen={isSmallScreen}
    />
  );

  const openRequest = () => {
    trackEvent("Pitch Local Button Clicked");
    navigate(`/${routes.pitchLocal}`);
  };

  const filtersList = (
    <>
      <SelectFilter
        label={"Partnership Status"}
        options={COMPANY_TYPES}
        params={SearchParams.TYPE_DISCOVER}
        setClose={setClose}
        close={close}
        isOptionSelected={isOptionSelected}
        selectedOptionIcon={selectedOptionIcon}
        iconsMap={companyTypeIcons}
        isSmallScreen={isSmallScreen}
        subgroups={[
          {
            label: "Works With",
            icon: (
              <Box
                component="i"
                className="fa-regular fa-handshake"
                sx={{ fontSize: 13, color: "text.secondary" }}
              />
            ),
            keys: [
              CompanyType.WorksWithUgc,
              CompanyType.WorksWithInfluencer,
              CompanyType.WorksWithPodcasters,
              CompanyType.WorksWithAgencies,
              CompanyType.WorksWithNewsletters,
            ],
          },
        ]}
      />
      <SelectFilter
        label={"Contact Status"}
        options={CONTACT_TYPES}
        params={SearchParams.CONTACT_DISCOVER}
        setClose={setClose}
        close={close}
        isOptionSelected={isOptionSelected}
        selectedOptionIcon={selectedOptionIcon}
        iconsMap={contactTypeIcons}
        isSmallScreen={isSmallScreen}
      />
      <LocationFilter
        handleAddOption={handleAddOption}
        close={close}
        setClose={setClose}
        selectedOptionIcon={selectedOptionIcon}
        isOptionSelected={isOptionSelected}
        isSmallScreen={isSmallScreen}
      />
      <SelectFilter
        label={"Instagram Following"}
        options={FOLLOWING_SIZES}
        params={SearchParams.FOLLOWING_DISCOVER}
        setClose={setClose}
        close={close}
        isOptionSelected={isOptionSelected}
        selectedOptionIcon={selectedOptionIcon}
        isSmallScreen={isSmallScreen}
      />
      <SelectFilter
        label={"Gender"}
        options={TARGET_GENDERS}
        params={SearchParams.GENDER_DISCOVER}
        setClose={setClose}
        close={close}
        isOptionSelected={isOptionSelected}
        selectedOptionIcon={selectedOptionIcon}
        isSmallScreen={isSmallScreen}
      />
      {(currentOrg?.isVaUser || currentOrg?.isAutopilotUser) && (
        <SelectFilter
          label={"Compensation Types"}
          options={COMPENSATION_TYPES}
          params={SearchParams.COMPENSATION_DISCOVER}
          setClose={setClose}
          close={close}
          isOptionSelected={isOptionSelected}
          selectedOptionIcon={selectedOptionIcon}
          isSmallScreen={isSmallScreen}
        />
      )}
      <SelectFilter
        label={"Languages"}
        options={LANGUAGES}
        params={SearchParams.LANGUAGE_DISCOVER}
        setClose={setClose}
        close={close}
        isOptionSelected={isOptionSelected}
        selectedOptionIcon={selectedOptionIcon}
        isSmallScreen={isSmallScreen}
      />
    </>
  );

  const buttons = newCampaignView ? undefined : (
    <Box sx={{ mr: "1px" }}>
      {currentOrg?.isVaUser && isAdmin && <AutomationVA />}
      {!isMobileScreen && (
        <Button
          size="small"
          disableElevation
          sx={styles.pitchLocalButton}
          onClick={openRequest}
        >
          Pitch Local 📍
        </Button>
      )}
    </Box>
  );

  if (isSmallScreen) {
    return (
      <DiscoverFilterMobile
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        filtersList={filtersList}
        sortFilter={newCampaignView ? undefined : sortFilter}
        totalCount={totalCount}
        brandsLoading={brandsLoading}
      />
    );
  } else {
    return (
      <DiscoverFilterDesktop
        buttons={buttons}
        filtersList={filtersList}
        sortFilter={newCampaignView ? undefined : sortFilter}
        displayedSelectedTags={displayedSelectedTags}
        newCampaignView={newCampaignView}
      />
    );
  }
}
